import { NoopScrollStrategy, Overlay } from '@angular/cdk/overlay';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, LOCALE_ID, NgModule } from '@angular/core';
import { MAT_MENU_SCROLL_STRATEGY } from '@angular/material/menu';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { DialogModule } from '@app/core/dialog/dialog.module';
import { HttpApiInterceptor } from '@core/interceptors/http-api.interceptor';
import { SafeHtmlPipe } from '@core/safe-html/safe-html.pipe';
import { SkipToMainModule } from '@core/skip-to-main/skip-to-main.module';
import { ToastModule } from '@core/toast/toast.module';
import { TranslateCompiler, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { BookingCalculationService } from '@roller/booking-calculation-service';
import { MESSAGE_FORMAT_CONFIG, TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { IAppConfig } from '@core/app-config/app-config.model';
import { AppConfigService } from '@core/app-config/app-config.service';
import { loadLocaleId } from './core/app-config/locale-id-config';
import { ContentPreloaderModule } from './core/content-preloader/content-preloader.component';
import { RollerTranslateLoaderService } from './core/translations/roller-translate-loader.service';
import { ErrorPageComponent } from '@app/error-page/error-page.component';
import { ProductCapacityService } from '@roller/product-capacity-service';
import { provideLottieOptions } from 'ngx-lottie';
import { CustomDateFnsAdapter } from '@core/date/custom-date-fns-adapter';
import { DateAdapter } from '@angular/material/core';

export function initApp(configurationService: AppConfigService): () => Promise<IAppConfig> {
  return (): Promise<IAppConfig> => configurationService.loadAppConfigs();
}

export function defaultScrollFactory(overlay: Overlay): () => NoopScrollStrategy {
  return () => overlay.scrollStrategies.noop();
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    AppRoutingModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: RollerTranslateLoaderService,
        deps: [AppConfigService, HttpClient]
      },
      defaultLanguage: 'en',
      compiler: {
        provide: TranslateCompiler,
        useClass: TranslateMessageFormatCompiler
      }
    }),
    ToastModule,
    SkipToMainModule,
    ErrorPageComponent,
    ContentPreloaderModule,
    DialogModule
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpApiInterceptor,
      multi: true
    },
    {
      provide: LOCALE_ID,
      useValue: loadLocaleId()
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initApp,
      multi: true,
      deps: [AppConfigService]
    },
    {
      provide: MESSAGE_FORMAT_CONFIG,
      useValue: {
        multi: true
      }
    },
    {
      provide: MAT_MENU_SCROLL_STRATEGY,
      useFactory: defaultScrollFactory,
      deps: [Overlay]
    },
    { provide: DateAdapter, useClass: CustomDateFnsAdapter },
    provideLottieOptions({
      player: () => import('lottie-web')
    }),
    SafeHtmlPipe,
    BookingCalculationService,
    ProductCapacityService,
    AppConfigService
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
